import { useEffect, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Rewind, FastForward } from "lucide-react";
import { toast } from "sonner";
import { usePlayer, fmtTime } from "@/context/PlayerContext";
import { JogWheel } from "./JogWheel";
import { Waveform } from "./Waveform";
import { LightsButton } from "./LightsButton";

const PAD_COLORS = ["#ff4fa3", "#38bdf8", "#1fe896", "#ffb547"];
const key = "rounded-md bg-[#0f1014] border border-white/15 font-mono-tga text-[9px] font-bold uppercase tracking-wider text-zinc-300 hover:border-white/50 hover:text-white active:scale-95 transition-[transform,border-color,color]";
const Label = ({ children }) => <span className="font-mono-tga text-[8px] uppercase tracking-[0.2em] text-zinc-500">{children}</span>;

const HotCues = ({ cues, onPad }) => (
  <div className="grid grid-cols-4 gap-1.5">
    {"ABCD".split("").map((l, i) => (
      <button key={l} onClick={() => onPad(i)} data-testid={`deck-hotcue-${l.toLowerCase()}`}
        className={`${key} h-8 relative overflow-hidden`} style={cues[i] != null ? { background: PAD_COLORS[i], color: "#0f1014", borderColor: PAD_COLORS[i], boxShadow: `0 0 14px ${PAD_COLORS[i]}88` } : undefined}>
        {l}{cues[i] != null && <span className="ml-1 opacity-70">{fmtTime(cues[i])}</span>}
      </button>
    ))}
  </div>
);

// Pioneer CDJ-3000 inspired multi-player: display, hot cues, loop, jog, tempo fader with master tempo.
export const PioneerDeck = ({ featured }) => {
  const p = usePlayer();
  const { current, playing, time, duration, rate, setRate, masterTempo, setMasterTempo } = p;
  const [cues, setCues] = useState({});
  const [loop, setLoop] = useState({ in: null, out: null, on: false });
  const shown = current || (featured && { ...(featured.tracks?.find((t) => t.audio_url) || {}), release_title: featured.title, artist_name: featured.artist_name, cover_url: featured.cover_url });
  const trackCues = cues[current?.id] || [];
  const progress = current && duration ? time / duration : 0;
  const tempo = (rate - 1) * 100;

  useEffect(() => { if (loop.on && loop.out != null && time >= loop.out) p.seek(loop.in); }, [time, loop]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLoop({ in: null, out: null, on: false }); }, [current?.id]);

  const start = (ratio = null) => {
    if (!featured || !p.playRelease(featured, Math.max(0, featured.tracks.findIndex((t) => t.audio_url)), ratio)) toast("Carica la prima uscita con audio per accendere il deck");
  };
  const onPlay = () => (current ? p.toggle() : start());
  const onSeek = (r) => (current && duration ? p.seek(r * duration) : start(r));
  const onPad = (i) => {
    if (!current) return toast("Avvia un brano per usare gli hot cue");
    if (trackCues[i] != null) return p.seek(trackCues[i]);
    const next = [...trackCues]; next[i] = time;
    setCues({ ...cues, [current.id]: next });
  };
  const bpm = shown?.bpm ? (parseFloat(shown.bpm) * rate).toFixed(1) : "--.-";

  return (
    <div className={`pioneer-body rounded-[26px] border border-white/10 p-4 sm:p-5 w-full max-w-[560px] ${p.lights ? "" : "float-y"}`} data-testid="cdj-deck">
      <div className="flex items-center justify-between mb-3">
        <span className="font-display text-[11px] font-black tracking-[0.25em] text-zinc-200">TGA <span className="text-zinc-400">CDJ-</span><span className="text-sky-400">3000</span></span>
        <div className="hidden sm:flex gap-1.5">{["Source", "Browse", "Tag list", "Playlist", "Search", "Menu"].map((l) => <span key={l} className="px-2 py-1 rounded bg-[#0f1014] border border-white/10 font-mono-tga text-[8px] uppercase text-zinc-500">{l}</span>)}</div>
        <div className="flex items-center gap-2">
          <LightsButton testid="deck-lights-btn" withLabel />
          <span className={`h-2.5 w-2.5 rounded-full ${playing ? "bg-sky-400 shadow-[0_0_10px_#38bdf8]" : "bg-zinc-700"}`} />
        </div>
      </div>

      <div className="lcd rounded-xl p-3 mb-3" data-testid="cdj-display">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-display text-base sm:text-lg font-bold text-white truncate" data-testid="cdj-track-title">{shown?.title || "Nessun brano caricato"}</p>
            <p className="text-xs text-sky-200/80 truncate" data-testid="cdj-track-artist">{shown?.artist_name || "The Groove Anomaly records"}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono-tga text-xl font-bold text-white leading-none" data-testid="cdj-bpm">{bpm}</p>
            <p className="font-mono-tga text-[9px] text-sky-300/80">BPM {shown?.key ? `· ${shown.key}` : ""}</p>
          </div>
        </div>
        <div className="mt-2"><Waveform seed={shown?.id || "tga"} progress={progress} onSeek={onSeek} active={playing} bars={100} height="h-14" variant="rgb" testid="cdj-waveform" /></div>
        <div className="mt-1.5"><Waveform seed={`${shown?.id || "tga"}-ov`} progress={progress} onSeek={onSeek} bars={160} height="h-4" variant="lcd" testid="cdj-overview" /></div>
        <div className="flex justify-between items-end font-mono-tga mt-2">
          <span className="text-amber-400 text-lg leading-none" data-testid="cdj-time-elapsed">{fmtTime(current ? time : 0)}</span>
          <span className={`text-[10px] ${masterTempo ? "text-hot" : "text-zinc-600"}`}>MT</span>
          <span className="text-[11px] text-sky-300" data-testid="cdj-tempo-display">{tempo >= 0 ? "+" : ""}{tempo.toFixed(2)}%</span>
          <span className="text-sky-300/80 text-lg leading-none" data-testid="cdj-time-remaining">-{fmtTime(current ? Math.max(duration - time, 0) : 0)}</span>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_auto] gap-2 mb-4">
        <HotCues cues={trackCues} onPad={onPad} />
        <div className="grid grid-cols-3 gap-1.5">
          <button onClick={() => current && setLoop({ in: time, out: null, on: false })} className={`${key} h-8 px-2 ${loop.in != null ? "text-amber-400 border-amber-500" : ""}`} data-testid="deck-loop-in">In</button>
          <button onClick={() => loop.in != null && time > loop.in && setLoop({ ...loop, out: time, on: true })} className={`${key} h-8 px-2 ${loop.on ? "text-amber-400 border-amber-500 animate-pulse" : ""}`} data-testid="deck-loop-out">Out</button>
          <button onClick={() => setLoop({ in: null, out: null, on: false })} className={`${key} h-8 px-2`} data-testid="deck-loop-exit">Exit</button>
        </div>
      </div>

      <div className="grid grid-cols-[auto_1fr_auto] gap-3 sm:gap-5 items-center">
        <div className="flex flex-col items-center gap-2.5">
          <Label>Track search</Label>
          <div className="flex gap-1.5">
            <button onClick={p.prev} aria-label="Brano precedente" data-testid="cdj-prev-btn" className={`${key} h-8 w-9 grid place-items-center`}><SkipBack className="h-3.5 w-3.5" /></button>
            <button onClick={p.next} aria-label="Brano successivo" data-testid="cdj-next-btn" className={`${key} h-8 w-9 grid place-items-center`}><SkipForward className="h-3.5 w-3.5" /></button>
          </div>
          <Label>Search</Label>
          <div className="flex gap-1.5">
            <button onClick={() => p.nudge(-5)} aria-label="Indietro 5 secondi" data-testid="deck-search-back" className={`${key} h-8 w-9 grid place-items-center`}><Rewind className="h-3.5 w-3.5" /></button>
            <button onClick={() => p.nudge(5)} aria-label="Avanti 5 secondi" data-testid="deck-search-fwd" className={`${key} h-8 w-9 grid place-items-center`}><FastForward className="h-3.5 w-3.5" /></button>
          </div>
          <button onClick={p.cue} aria-label="Cue" data-testid="cdj-cue-btn" className="mt-1 h-14 w-14 rounded-full bg-[#0f1014] border-[3px] border-amber-500 font-mono-tga text-xs font-bold text-amber-400 shadow-[0_0_16px_rgba(255,159,28,0.35)] hover:scale-105 active:scale-95 transition-transform">CUE</button>
          <button onClick={onPlay} aria-label={playing ? "Pausa" : "Play"} data-testid="cdj-play-btn"
            className={`h-14 w-14 rounded-full bg-[#0f1014] border-[3px] border-emerald-400 grid place-items-center hover:scale-105 active:scale-95 transition-transform ${playing ? "glow-pulse" : ""}`}>
            {playing ? <Pause className="h-5 w-5 text-emerald-400" /> : <Play className="h-5 w-5 ml-0.5 text-emerald-400" />}
          </button>
        </div>
        <div className="grid place-items-center">
          <JogWheel cover={shown?.cover_url} spinning={playing} onScrub={current ? p.nudge : undefined} size="w-full max-w-[250px] aspect-square" testid="cdj-jog-wheel" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <button onClick={() => setMasterTempo(!masterTempo)} className={`${key} h-7 px-2 ${masterTempo ? "text-hot border-hot" : ""}`} data-testid="deck-master-tempo">Master tempo</button>
          <Label>Tempo ±8</Label>
          <div className="relative h-40 w-10 rounded-md bg-[#0f1014] border border-white/15">
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 h-px w-6 bg-emerald-400" />
            <input type="range" min={-8} max={8} step={0.1} value={tempo} onChange={(e) => setRate(1 + parseFloat(e.target.value) / 100)} aria-label="Tempo" data-testid="deck-tempo-fader"
              className="absolute left-1/2 top-1/2 w-36 -translate-x-1/2 -translate-y-1/2 rotate-90 accent-zinc-200 cursor-pointer" />
          </div>
          <button onClick={() => setRate(1)} className={`${key} h-7 px-2 ${rate === 1 ? "text-emerald-400 border-emerald-500" : ""}`} data-testid="deck-tempo-reset">Tempo reset</button>
        </div>
      </div>
    </div>
  );
};
